/**
 * useLookupCacheWriteThrough — refresh every lookup enum cache that reads from
 * a table the user just edited.
 *
 * A lookup enum's labels live in ANOTHER table. Rename a status row and the
 * order grid keeps rendering the old name: nothing about the orders column
 * changed, so neither the miss-driven self-heal (the keys are all still known)
 * nor a re-query can notice. The one moment the app *knows* the pairs are
 * wrong is right after it has written to the target table itself — so that is
 * where the cache is refreshed.
 *
 * Cheap by construction: the first thing it does is scan the MetaInfo snapshot
 * the workspace already holds for anything pointing at the edited table.
 * Almost every edit is to a table nothing looks up, and those cost two array
 * scans and zero queries.
 *
 * Two things can point at it, and both are matched:
 *   - a `source: 'lookup'` **declaration** whose `lookup.toTable` is the edited
 *     table — the explicit case, and the only match a cache written before the
 *     `lookup` stamp existed can get;
 *   - a pair **cache** whose own `lookup` stamp names it — which is all an
 *     *implicit* lookup enum (reference-table flag, no declaration anywhere)
 *     ever leaves behind, and what future explicit caches carry too.
 * The two sets are unioned and deduped per source column, so a column matched
 * both ways is still refreshed exactly once.
 *
 * Wired at the row-mutation SUCCESS sites — `TableExplorerTab`'s quick edit
 * (which is also where the row-details panel and the single-row view commit)
 * and its batch save, plus the Query Editor's results-editing save.
 */

import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DatabaseMetaInfo, EnumLookupTarget } from '@/types/electron';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { TrackFunction } from '../../../../core/analytics/analyticsTypes';
import { fetchMetaInfo } from '../services/metaInfoApi';
import { refreshLookupCacheForColumn } from '../utils/lookupCacheRefresh';
import { metaInfoQueryKey, useInvalidateMetaInfo } from './useMetaInfo';

const FIVE_MINUTES = 5 * 60 * 1000;

/** The table a mutation was committed against. */
export type EditedTableIdentity = {
  /** `''` on schemaless engines (Kusto) — compared `??`-normalised, never `!schema`. */
  schema?: string | null;
  table?: string | null;
};

/** One column whose pair cache reads from the edited table. */
type LookupColumnTargeting = {
  schema: string;
  table: string;
  column: string;
  target: EnumLookupTarget;
};

/**
 * Every lookup column reading from `(schema, table)` — declarations first,
 * then cache stamps for columns no declaration covers — deduped per column.
 * Pure, and the cheap early-exit the whole helper hangs on: two array scans
 * over a snapshot the workspace already holds, and zero queries on a miss.
 */
function lookupColumnsTargeting(
  metaInfo: DatabaseMetaInfo | null | undefined,
  schema: string | null | undefined,
  table: string,
): LookupColumnTargeting[] {
  const wantedSchema = schema ?? '';
  const seen = new Set<string>();
  const matches: LookupColumnTargeting[] = [];
  const targetsEditedTable = (target: EnumLookupTarget | undefined): boolean =>
    !!target && target.toTable === table && (target.toSchema ?? '') === wantedSchema;
  const push = (
    entry: { schema: string; table: string; column: string },
    target: EnumLookupTarget,
  ) => {
    const key = `${entry.schema}|${entry.table}|${entry.column}`;
    if (seen.has(key)) return;
    seen.add(key);
    matches.push({ schema: entry.schema, table: entry.table, column: entry.column, target });
  };

  for (const declaration of metaInfo?.enumColumns ?? []) {
    // Lookup enums are a COLUMN-level concept — a JSON sub-key never has one.
    if (declaration.source !== 'lookup' || declaration.path !== undefined) continue;
    if (!targetsEditedTable(declaration.lookup)) continue;
    push(declaration, declaration.lookup!);
  }

  // The stamp every lookup fetch leaves on the cache row it writes. It is the
  // ONLY trace an implicit lookup enum has, and it covers explicit columns too
  // — they are already deduped away above.
  for (const cache of metaInfo?.enumValueCache ?? []) {
    if (cache.source !== 'lookup' || cache.path !== undefined) continue;
    if (!targetsEditedTable(cache.lookup)) continue;
    push(cache, cache.lookup!);
  }

  return matches;
}

/**
 * Refresh the pair caches of every lookup column reading from `(schema, table)`.
 * Resolves when the attempts are over; never throws, never reports failure.
 */
export async function refreshLookupCachesForTable(args: {
  queryClient: QueryClient;
  connectionId: string | null | undefined;
  database: string | null | undefined;
  engine: string | null | undefined;
  /** Identity of the table that was just written to. */
  schema?: string | null;
  table?: string | null;
  track?: TrackFunction;
  /** Defaults to invalidating the MetaInfo queries through `queryClient`. */
  invalidate?: (connectionId: string, database: string) => void;
}): Promise<void> {
  const { queryClient, connectionId, database, engine, schema, table, track } = args;
  if (!connectionId || !database || !engine || !table) return;

  const key = metaInfoQueryKey(connectionId, database);
  // The workspace already holds this snapshot (both grids read it); the fetch
  // is the rare cold path, and it is the same query the rest of the app uses.
  let metaInfo = queryClient.getQueryData<DatabaseMetaInfo | null>(key);
  if (metaInfo === undefined) {
    try {
      metaInfo = await queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => fetchMetaInfo(connectionId, database),
        staleTime: FIVE_MINUTES,
      });
    } catch {
      return;
    }
  }

  const columns = lookupColumnsTargeting(metaInfo, schema, table);
  if (columns.length === 0) return;

  const invalidate =
    args.invalidate ??
    ((c: string, d: string) => {
      void queryClient.invalidateQueries({ queryKey: metaInfoQueryKey(c, d) });
      void queryClient.invalidateQueries({ queryKey: ['metainfo', 'all'] });
    });

  await Promise.all(
    columns.map((match) =>
      refreshLookupCacheForColumn({
        queryClient,
        connectionId,
        database,
        engine,
        schema: match.schema,
        table: match.table,
        column: match.column,
        target: match.target,
        // Re-read at fetch time: the label column may have been changed in the
        // same session the row was edited in.
        getMetaInfo: () => queryClient.getQueryData<DatabaseMetaInfo | null>(key) ?? metaInfo,
        reason: 'write-through',
        // A committed write is not *evidence* of staleness the way a missing
        // key is — it IS staleness, so it must not be swallowed by a recent
        // miss-driven attempt on the same column.
        force: true,
        track,
        invalidate,
      }),
    ),
  );
}

/**
 * Component-facing wrapper: a stable callback to fire after a committed
 * insert / update / delete. Fire-and-forget — the caller never awaits it, and
 * a failure is invisible by design.
 */
export function useLookupCacheWriteThrough(args: {
  connectionId: string | null | undefined;
  database: string | null | undefined;
  engine: string | null | undefined;
}): (edited: EditedTableIdentity) => void {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const invalidate = useInvalidateMetaInfo();

  // Read at call time, never through the callback's identity: the returned
  // function stays stable, so the save handlers that depend on it do too.
  const latest = useRef({ ...args, track, invalidate });
  latest.current = { ...args, track, invalidate };

  return useCallback(
    (edited: EditedTableIdentity) => {
      const { connectionId, database, engine, track: t, invalidate: inv } = latest.current;
      void refreshLookupCachesForTable({
        queryClient,
        connectionId,
        database,
        engine,
        schema: edited.schema,
        table: edited.table,
        track: t,
        invalidate: inv,
      });
    },
    [queryClient],
  );
}
